import React, {useState, useRef} from 'react';
import {Typography, Box, Grid} from '@mui/material';
import {FileUpload, HighlightOff, TextSnippet} from '@mui/icons-material';
import {
    parseAndValidateRnaCsv,
    parseValidSamples,
    mapSamplenameToFilenames,
    parseMetadataCsv,
    parseChipFilenamesCsv,
    validateChipReplicates,
    validateChipCsv
} from './ParseCsv';

interface ChooseFileProps {
    handleOnClick: () => void;
    uploadDescription: string;
    acceptedFileTypes: string[];
}

/**
 * @param handleOnClick Function to call when the user clicks on the component. Expected to open a file picker.
 */
const ChooseFile = ({handleOnClick, uploadDescription, acceptedFileTypes}: ChooseFileProps) => (
    <Box
        bgcolor="surface-container-low"
        border="1px dashed"
        borderColor="outline-variant"
        justifyContent="center"
        textAlign="center"
        borderRadius="12px"
        margin="2% 0"
        padding="2%"
        sx={{cursor: 'pointer'}}
        onClick={handleOnClick}>
        <FileUpload />

        <Typography variant="body" size="medium">
            {uploadDescription}
        </Typography>
        <Typography variant="body" size="small">
            Supports {acceptedFileTypes.map(ft => '.' + ft).join(', ')} files
        </Typography>
    </Box>
);

interface FileChosenProps {
    selectedCsv: File | null;
    handleRemoveFile: (e: React.MouseEvent<SVGSVGElement>) => void;
}

const FileChosen = ({selectedCsv, handleRemoveFile}: FileChosenProps) => (
    <Box
        justifyContent="space-between"
        bgcolor="surface-container-low"
        border="1px dashed"
        gap="14px"
        borderColor="outline-variant"
        display="flex"
        borderRadius="12px"
        margin="2% 0"
        padding="4.5%">
        <Box display="flex" gap="14px" overflow="auto">
            <TextSnippet />
            <Typography variant="body" size="large">
                {selectedCsv?.name}
            </Typography>
        </Box>

        <HighlightOff sx={{cursor: 'pointer'}} onClick={handleRemoveFile} />
    </Box>
);

interface UploadRnaCsvProps {
    setParsedSamples: (samples: Record<string, string>[]) => void;
    setSamplesToFilenames: (samplesToFilenames: Record<string, string[]>) => void;
    setCsvError: (error: string) => void;
    sequencingReadType: 'paired' | 'singled';
}

const UploadRnaCsv = ({setParsedSamples, setSamplesToFilenames, setCsvError, sequencingReadType}: UploadRnaCsvProps) => {
    const [selectedCsv, setSelectedCsv] = useState<File | null>(null);
    const csvRef = useRef<HTMLInputElement>(null);

    const handleRemoveFile = (e: React.MouseEvent<SVGSVGElement>): void => {
        e.stopPropagation();
        // Explicitly clear the file input, otherwise you may get in a state
        // were the state is empty but the file input still has a file
        // selected. This becomes problematic if the user tries to upload the
        // same file again, because the onChange won't trigger.
        csvRef.current!.value = '';
        setSelectedCsv(null);
        setCsvError('');
        setParsedSamples([]);
        setSamplesToFilenames({});
    };

    /**
     * Parses the uploaded CSV file and adds the parsed contents to the parent
     * state.
     */
    const handleCSVUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setCsvError('');
        const filesList = event.target.files;
        const file = filesList?.item(0);
        if (!file) {
            setCsvError('No file selected');
            return;
        }

        setSelectedCsv(file);

        parseAndValidateRnaCsv(file, sequencingReadType)
            .then((contents: Record<string, string>[]) => {
                const parsedSamples = parseValidSamples(contents);
                const samplesToFilenames = mapSamplenameToFilenames(contents);
                setParsedSamples(parsedSamples);
                setSamplesToFilenames(samplesToFilenames);
            })
            .catch((errormsgs: string[]) => {
                setCsvError(errormsgs.join(', '));
            });

        // Clear the input value to enable re-uploading the same file.
        // Otherwise no change is detected and this event does not trigger.
        // Note that this assigns to `value`, `files` is readonly.
        event.target.value = '';
    };

    return (
        <Box>
            {!selectedCsv ? (
                <ChooseFile handleOnClick={() => csvRef.current?.click()} uploadDescription="Upload CSV file" acceptedFileTypes={['csv']} />
            ) : (
                <FileChosen selectedCsv={selectedCsv} handleRemoveFile={handleRemoveFile} />
            )}
            <input
                ref={csvRef}
                type="file"
                accept=".csv"
                style={{display: 'none'}}
                onChange={handleCSVUpload}
                data-testid="rna-csv-input"
            />
        </Box>
    );
};

interface UploadCsvProps {
    setParsedSamples: (samples: Record<string, string>[]) => void;
    setSamplesToFilenames: (samplesToFilenames: Record<string, string[]>) => void;
    setCsvError: (error: string) => void;
    sequencingType: string;
    sequencingReadType: 'paired' | 'singled';
    filesPerSample: number;
}

const UploadCsv = ({
    setParsedSamples,
    setSamplesToFilenames,
    setCsvError,
    sequencingType,
    sequencingReadType,
    filesPerSample
}: UploadCsvProps) => {
    const [metadataCsv, setMetadataCsv] = useState<File | null>(null);
    const [filenamesCsv, setFilenamesCsv] = useState<File | null>(null);
    const metadataRef = useRef<HTMLInputElement>(null);
    const filenamesRef = useRef<HTMLInputElement>(null);

    const handleRemoveFile = (e: React.MouseEvent<SVGSVGElement>, type: 'metadata' | 'filenames'): void => {
        e.stopPropagation();
        if (type === 'metadata') {
            metadataRef.current!.value = '';
            setMetadataCsv(null);
        } else {
            filenamesRef.current!.value = '';
            setFilenamesCsv(null);
        }
        setCsvError('');
        setParsedSamples([]);
        setSamplesToFilenames({});
    };

    /**
     * Normalises whatever is thrown by a parse function into an array of
     * human-readable error strings.  Parse functions are expected to throw
     * string[], but PapaParse's internal error callback rejects with an Error
     * object, which is not iterable and cannot be safely spread.
     */
    const toErrorMessages = (thrown: unknown): string[] => {
        if (Array.isArray(thrown)) return thrown as string[];
        if (thrown instanceof Error) return [thrown.message];
        return [String(thrown)];
    };

    const handleCSVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setCsvError('');
        const filesList = e.target.files;
        const file = filesList?.item(0);
        if (!file) {
            setCsvError('No file selected');
            return;
        }

        // Don't think we can rely on React State being updated immediately, so
        // local variables to use for parsing further down
        let metadataF = metadataCsv;
        let filenamesF = filenamesCsv;
        // Update state
        if (e.target === metadataRef.current) {
            setMetadataCsv(file);
            metadataF = file;
        } else if (e.target === filenamesRef.current) {
            setFilenamesCsv(file);
            filenamesF = file;
        }

        // Parse when both files are uploaded
        // Do this here to avoid needing a useEffect and possible extra rendering etc
        if (metadataF && filenamesF) {
            const errors: string[] = [];

            // Parse each CSV file, collect errors
            let metadata: Record<string, string>[] | null = null;
            let filenames: Record<string, string[]> | null = null;
            try {
                metadata = await parseMetadataCsv(metadataF, sequencingType as 'ChIP Seq' | 'ATAC Seq');
            } catch (errormsgs: unknown) {
                errors.push(...toErrorMessages(errormsgs));
            }
            // the following validation and parsing is reused for atac and chip
            try {
                filenames = await parseChipFilenamesCsv(filenamesF, sequencingReadType, filesPerSample);
            } catch (errormsgs: unknown) {
                errors.push(...toErrorMessages(errormsgs));
            }
            if (errors.length > 0) {
                setCsvError(errors.join(', '));
                return;
            }

            const replicateValidation = validateChipReplicates(metadata!);
            if (replicateValidation.status === 'error') {
                setCsvError(replicateValidation.messages.join(', '));
                return;
            }

            const comboValidation = validateChipCsv(metadata!, filenames!);
            if (comboValidation.status === 'error') {
                setCsvError(comboValidation.messages.join(', '));
                return;
            }

            setParsedSamples(metadata!);
            setSamplesToFilenames(filenames!);
        }

        e.target.value = '';
    };

    return (
        <Grid container direction="row" spacing={2}>
            <Grid item xs={6}>
                {!metadataCsv ? (
                    <ChooseFile
                        handleOnClick={() => metadataRef.current?.click()}
                        uploadDescription="Upload metadata file"
                        acceptedFileTypes={['csv']}
                    />
                ) : (
                    <FileChosen selectedCsv={metadataCsv} handleRemoveFile={e => handleRemoveFile(e, 'metadata')} />
                )}
                <input
                    ref={metadataRef}
                    type="file"
                    accept=".csv"
                    style={{display: 'none'}}
                    onChange={handleCSVUpload}
                    data-testid="chip-metadata-input"
                />
            </Grid>
            <Grid item xs={6}>
                {!filenamesCsv ? (
                    <ChooseFile
                        handleOnClick={() => filenamesRef.current?.click()}
                        uploadDescription="Upload filenames file"
                        acceptedFileTypes={['csv']}
                    />
                ) : (
                    <FileChosen selectedCsv={filenamesCsv} handleRemoveFile={e => handleRemoveFile(e, 'filenames')} />
                )}
                <input
                    ref={filenamesRef}
                    type="file"
                    accept=".csv"
                    style={{display: 'none'}}
                    onChange={handleCSVUpload}
                    data-testid="chip-filenames-input"
                />
            </Grid>
        </Grid>
    );
};

interface UploadCsvsProps {
    sequencingType: string;
    sequencingReadType: 'paired' | 'singled';
    filesPerSample: number;
    setParsedSamples: (samples: Record<string, string>[]) => void;
    setSamplesToFilenames: (samplesToFilenames: Record<string, string[]>) => void;
    setCsvError: (error: string) => void;
}

/**
 * Enables uploading a sample CSV for the experiment.
 *
 * For RNA Seq, this is a single template. For ChIP and ATAC Seq, there are two: one is
 * the experimental design, the other is the filenames.
 *
 * RNA seq does not use filesPerSample or sequencingReadType
 */
export const UploadCsvs = ({
    sequencingType,
    sequencingReadType,
    filesPerSample,
    setParsedSamples,
    setSamplesToFilenames,
    setCsvError
}: UploadCsvsProps) => {
    if (sequencingType === 'RNA Seq') {
        return (
            <UploadRnaCsv
                setParsedSamples={setParsedSamples}
                setSamplesToFilenames={setSamplesToFilenames}
                setCsvError={setCsvError}
                sequencingReadType={sequencingReadType}
            />
        );
    } else if (['ChIP Seq', 'ATAC Seq'].includes(sequencingType)) {
        return (
            <UploadCsv
                setParsedSamples={setParsedSamples}
                setSamplesToFilenames={setSamplesToFilenames}
                setCsvError={setCsvError}
                sequencingType={sequencingType}
                sequencingReadType={sequencingReadType}
                filesPerSample={filesPerSample}
            />
        );
    }
    return null;
};
